import React, { useState } from 'react';
import { IoPersonCircle, IoSparkles, IoChevronDown, IoChevronUp } from 'react-icons/io5';
import { CText } from '../../../components/common';
import { Message } from '../../../api/chat';
import SourceCard from './SourceCard';

interface ChatMessageProps {
  message: Message;
  colors: any;
}

const ChatMessage: React.FC<ChatMessageProps> = ({ message, colors }) => {
  const [showSources, setShowSources] = useState(false);
  const isUser = message.role === 'user';

  const formatTime = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  };

  // Simple markdown-like rendering
  const renderText = (text: string) => {
    // Split by code blocks
    const parts = text.split(/(```[\s\S]*?```)/g);

    return parts.map((part, index) => {
      if (part.startsWith('```') && part.endsWith('```')) {
        // Code block
        const code = part.slice(3, -3).replace(/^\w+\n/, ''); // Remove language identifier
        return (
          <pre
            key={index}
            className="p-3 rounded-lg my-2 overflow-x-auto text-sm"
            style={{
              backgroundColor: colors.backgroundColor,
              border: `1px solid ${colors.borderColor}`,
            }}
          >
            <code>{code}</code>
          </pre>
        );
      }

      // Regular text with basic formatting
      return (
        <span key={index}>
          {part.split('\n').map((line, lineIdx) => {
            // Bold text
            let formatted = line.replace(
              /\*\*(.*?)\*\*/g,
              '<strong>$1</strong>'
            );
            // Italic text
            formatted = formatted.replace(/\*(.*?)\*/g, '<em>$1</em>');

            return (
              <React.Fragment key={lineIdx}>
                {lineIdx > 0 && <br />}
                <span dangerouslySetInnerHTML={{ __html: formatted }} />
              </React.Fragment>
            );
          })}
        </span>
      );
    });
  };

  return (
    <div
      className={`flex ${isUser ? 'justify-end' : 'justify-start'} mb-4`}
    >
      <div
        className={`flex ${isUser ? 'flex-row-reverse' : 'flex-row'} items-start gap-3 max-w-[85%]`}
      >
        {/* Avatar */}
        <div
          className="w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0"
          style={{
            backgroundColor: isUser ? colors.primary : colors.grayScale400,
          }}
        >
          {isUser ? (
            React.createElement(IoPersonCircle as any, { size: 20, color: colors.white })
          ) : (
            React.createElement(IoSparkles as any, { size: 16, color: colors.white })
          )}
        </div>

        {/* Message Content */}
        <div className="flex flex-col">
          <div
            className={`p-4 rounded-2xl ${isUser ? 'rounded-tr-sm' : 'rounded-tl-sm'}`}
            style={{
              backgroundColor: isUser ? colors.primary : colors.inputBackground,
              border: isUser ? 'none' : `1px solid ${colors.borderColor}`,
            }}
          >
            <CText
              type="r14"
              color={isUser ? colors.white : colors.textColor}
              style={{ lineHeight: 1.6 }}
            >
              {renderText(message.text)}
            </CText>
          </div>

          {/* Timestamp */}
          <CText
            type="r12"
            color={colors.grayScale400}
            style={{
              marginTop: 4,
              textAlign: isUser ? 'right' : 'left',
            }}
          >
            {formatTime(message.created_at)}
          </CText>

          {/* Sources (only for assistant messages) */}
          {!isUser && message.sources && message.sources.length > 0 && (
            <div className="mt-3">
              <button
                onClick={() => setShowSources(!showSources)}
                className="flex items-center gap-2 px-3 py-2 rounded-lg transition-colors"
                style={{
                  backgroundColor: colors.backgroundColor,
                  border: `1px solid ${colors.borderColor}`,
                }}
              >
                <CText type="r12" color={colors.grayScale500}>
                  {message.sources.length} sources
                </CText>
                {showSources ? (
                  React.createElement(IoChevronUp as any, { size: 14, color: colors.grayScale500 })
                ) : (
                  React.createElement(IoChevronDown as any, { size: 14, color: colors.grayScale500 })
                )}
              </button>

              {showSources && (
                <div className="flex flex-wrap gap-2 mt-2">
                  {message.sources.map((source, idx) => (
                    <SourceCard key={idx} source={source} colors={colors} />
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ChatMessage;
